import { Hashtag } from "iconsax-react";
import { Button } from "@/shared/ui";
import { EmptyState } from "@/shared/components";
import { extractErrorMessage } from "@/shared/utils";
import { useChatPage } from "../hooks";
import { ChatSidebar, ChatPageSkeleton, ChatChannelManagementModal } from "../components";

export default function ChatPage() {
  const {
    t,
    channels,
    selectedChannelId,
    selectedChannel,
    iframeUrl,
    isLoading,
    isError,
    error,
    refetch,
    canManage,
    isManageOpen,
    openManage,
    closeManage,
    handleSelectChannel,
  } = useChatPage();

  if (isLoading && channels.length === 0) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <ChatPageSkeleton />
      </>
    );
  }

  if (isError && channels.length === 0) {
    return (
      <>
        <title>{t("meta.title")}</title>
        <section className="flex-1 flex flex-col">
          <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("title")}</h1>
          </div>
          <div className="flex-1 flex flex-col items-center justify-center gap-4">
            <p className="text-heading-md content-base-primary">{t("error.title")}</p>
            <p className="text-body-regular-md content-base-secondary">
              {extractErrorMessage(error, "error.defaultText", t)}
            </p>
            <Button variant="secondary" size="md" onClick={() => refetch()}>
              {t("error.retry")}
            </Button>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.title")}</title>
      <section className="flex-1 flex flex-col">
        <div className="flex items-center justify-between pt-1 pb-4 border-b surface-base-stroke mb-5">
          <h1 className="text-display-xs content-base-primary">{t("title")}</h1>
        </div>
        <div className="flex-1 grid grid-cols-[1fr_3fr] min-h-0 radius-lg overflow-hidden">
          <ChatSidebar
            channels={channels}
            selectedChannelId={selectedChannelId}
            onSelectChannel={handleSelectChannel}
            canManage={canManage}
            onManageClick={openManage}
          />
          <div className="flex flex-col h-full">
            {selectedChannel && iframeUrl ? (
              <>
                <div className="px-2 py-4 border-b surface-base-fill surface-component-stroke shrink-0">
                  <div className="flex items-center justify-start gap-2">
                    <span className="content-base-primary">
                      <Hashtag color="currentColor" size={20} />
                    </span>
                    <h2 className="text-heading-md content-base-primary">{selectedChannel.display_name}</h2>
                  </div>
                </div>
                <iframe
                  key={selectedChannelId}
                  src={iframeUrl}
                  className="flex-1 w-full border-0 p-3 bg-white"
                  allow="clipboard-read; clipboard-write; microphone; camera; fullscreen; autoplay"
                  title={t("iframeTitle")}
                />
              </>
            ) : canManage ? (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 px-8 text-center surface-base-fill">
                <p className="text-heading-md content-base-primary">{t("emptyState.title")}</p>
                <p className="text-body-regular-md content-base-secondary">{t("emptyState.text")}</p>
                <Button variant="primary" size="md" onClick={openManage}>
                  {t("manageChannels")}
                </Button>
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center surface-base-fill">
                <EmptyState
                  noDataText={t("emptyState.noAccessTitle")}
                  noDataHint={t("emptyState.noAccessHint")}
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {isManageOpen && <ChatChannelManagementModal onClose={closeManage} />}
    </>
  );
}
